import { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { DropDown } from '@components/DropDown'
import { NavBar, NavItemType } from '@components/NavBar'
import { Button } from '@components/Button'

import libraryIcon from '@assets/images/library-icon.svg'
import taskIcon from '@assets/images/task-icon.svg'
import sequenceIcon from '@assets/images/sequence-icon.svg'
import penIcon from '@assets/images/pen-icon.svg'

import CloseIcon from '@assets/images/close-icon.svg?react'

import { TaskTemplateList } from './TaskTemplateList'
import { SequenceTemplateList } from './SequenceTemplateList'

import {
  ButtonLibrary,
  LibraryImg,
  Container,
  HeaderTitle,
  ButtonClose,
  HeaderContainer,
  BodyContainer,
  ListContainer,
  NavContainer,
  ButtonContainer,
  PenIcon,
} from './DropDownLibrary.styled'

interface Props {
  library?: 'task' | 'sequence'
}

export const DropDownLibrary = (props: Props): JSX.Element => {
  const { library } = props

  const navigate = useNavigate()

  const { projectId } = useParams()

  const { t, i18n } = useTranslation()

  const refElement = useRef<HTMLButtonElement | null>()

  const getContainerHeight = (): number | undefined => {
    const rect = refElement.current?.getBoundingClientRect()

    if (!rect) {
      return undefined
    }

    return window.innerHeight - rect.top - rect.height - 16
  }

  const items: NavItemType[] = [
    {
      key: 'tasks',
      label: 'Tâches',
      icon: () => <img src={taskIcon} />,
      style: {
        width: 75,
      },
    },
    {
      key: 'sequences',
      label: 'Séquences',
      icon: () => <img src={sequenceIcon} />,
      style: {
        width: 100,
      },
    },
  ]

  const [selectedItem, setSelectedItem] = useState<NavItemType>(items[0])

  const handleAddClick = () => {
    if (!projectId) return

    const path = `/${i18n.language}/projects/${projectId}/library`
    const tasksPath = `${path}/task-templates`
    const sequencesPath = `${path}/sequence-templates`

    navigate(selectedItem.key === 'tasks' ? tasksPath : sequencesPath)
  }

  return (
    <DropDown
      position="right"
      disabled
      WrappedComponent={({ show }) => (
        <ButtonLibrary ref={(ref) => (refElement.current = ref)} onClick={show} type="button">
          <LibraryImg src={libraryIcon} />

          {t('modules_project-settings_components_DropDownLibrary')}
        </ButtonLibrary>
      )}
      dropDownWidth={296}
      WrappedChildren={({ hide }) => (
        <Container id="library" style={{ height: getContainerHeight() }}>
          <HeaderContainer>
            <HeaderTitle>{t('modules_project-settings_components_DropDownLibrary')}</HeaderTitle>
            <ButtonClose onClick={hide}>
              <CloseIcon fill="#4D5874" />
            </ButtonClose>
          </HeaderContainer>

          <BodyContainer>
            {!library && (
              <NavContainer>
                <NavBar items={items} selected={selectedItem} onClick={setSelectedItem} />
              </NavContainer>
            )}

            <ListContainer navBarIsHidden={!!library} hidden={selectedItem.key != 'tasks'}>
              <TaskTemplateList />
            </ListContainer>
            <ListContainer navBarIsHidden={!!library} hidden={selectedItem.key != 'sequences'}>
              <SequenceTemplateList />
            </ListContainer>
            {projectId && (
              <ButtonContainer>
                <Button onClick={handleAddClick} variant="outline" size="medium" color="neutral">
                  <PenIcon src={penIcon} />
                  {selectedItem.key === 'tasks'
                    ? t('modules_project-settings_components_DropDownLibrary_edit_task')
                    : t('modules_project-settings_components_DropDownLibrary_edit_sequence')}
                </Button>
              </ButtonContainer>
            )}
          </BodyContainer>
        </Container>
      )}
    />
  )
}
